import { useState, useEffect } from 'react';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

/** Cache por unidade — idoc_enabled é flag de clinic_settings.unit_id. */
const cachedByUnit = new Map<string, boolean>();
const inflightByUnit = new Map<string, Promise<boolean>>();

async function fetchIdocEnabledForUnit(unitId: string): Promise<boolean> {
  if (cachedByUnit.has(unitId)) return cachedByUnit.get(unitId)!;

  let promise = inflightByUnit.get(unitId);
  if (!promise) {
    promise = axios
      .get(getApiUrl('/clinic/settings'), {
        headers: { 'X-Unit-Id': unitId },
      })
      .then((res) => {
        const enabled = res.data?.data?.idoc_enabled === true;
        cachedByUnit.set(unitId, enabled);
        return enabled;
      })
      .catch(() => {
        cachedByUnit.set(unitId, false);
        return false;
      })
      .finally(() => {
        inflightByUnit.delete(unitId);
      });
    inflightByUnit.set(unitId, promise);
  }
  return promise;
}

export function invalidateIdocEnabledCache(unitId?: string | null): void {
  if (unitId) {
    cachedByUnit.delete(unitId);
    inflightByUnit.delete(unitId);
    return;
  }
  cachedByUnit.clear();
  inflightByUnit.clear();
}

/** Indica se a aba iDoc (Radio Memory) está ativa na unidade corrente. */
export function useIdocEnabled(): { idocEnabled: boolean; loading: boolean } {
  const { user } = useAuth();
  const unitId = user?.current_unit?.id ?? null;

  const [idocEnabled, setIdocEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      if (!unitId) {
        setIdocEnabled(false);
        setLoading(Boolean(user));
        return;
      }
      setLoading(true);
      fetchIdocEnabledForUnit(unitId).then((enabled) => {
        if (cancelled) return;
        setIdocEnabled(enabled);
        setLoading(false);
      });
    };

    load();

    const reload = () => {
      if (unitId) invalidateIdocEnabledCache(unitId);
      else invalidateIdocEnabledCache();
      load();
    };
    window.addEventListener('unit:changed', reload);
    window.addEventListener('clinic-settings:saved', reload);
    return () => {
      cancelled = true;
      window.removeEventListener('unit:changed', reload);
      window.removeEventListener('clinic-settings:saved', reload);
    };
  }, [unitId, user]);

  return { idocEnabled, loading };
}

export default useIdocEnabled;
